import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  patchState,
  signalStore,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, tap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  setPropError,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { APP_BIOMETRIC_LOGIN } from '@application/config';
import { IPerson } from '@domains/person/register/data';
import { AuthStore } from './auth.store';
import { BiometricService } from './infra/biometric.service';
import { IAuthAccessToken } from './entities/authentication.interface';
import { IBiometricInfo } from './entities/biometric.model';

interface IBiometricState {
  /** Device has usable biometric hardware enrolled. */
  available: boolean;
  /** User passed biometrics in the current app session. */
  unlocked: boolean;
  /** Biometric login is turned on (persisted in localStorage). */
  enabled: boolean;
}

const initialState: IBiometricState = {
  available: false,
  unlocked: false,
  enabled: false,
};

/** Composes the display name a person object exposes across its optional fields. */
const personDisplayName = (person: IPerson): string | null => {
  const composed = [person.FirstName, person.LastName].filter(Boolean).join(' ').trim();
  return person.FullName?.trim() || composed || person.DisplayName || person.FirstName || null;
};

/**
 * State + orchestration for the native biometric login. Components, guards and the
 * `AuthTokenReceiverComponent` talk to this store; it delegates the native plugin work
 * to {@link BiometricService}. Async operations expose `unlock*`/`enable*` req-state
 * signals (loading/loaded/error).
 *
 * The whole session ({@link IAuthAccessToken}) is the biometric-protected secret, so the
 * short-lived access token is refreshed normally and {@link updateStoredSession} keeps
 * the Keychain copy fresh. Logout {@link lock}s (keeps the enrollment); only a fresh
 * password login ({@link offerAfterLogin}) or {@link disable} clears it.
 *
 * Dormant unless the app opted in (`APP_BIOMETRIC_LOGIN`): enrollment is only ever
 * offered from the opt-in gated `AuthTokenReceiverComponent`, so `enabled` stays false
 * everywhere else and every method below no-ops.
 */
export const BiometricStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withReqState('unlock', 'enable'),
  withMethods(
    (
      store,
      service = inject(BiometricService),
      authStore = inject(AuthStore),
      translate = inject(TranslateService)
    ) => {
      const checkAvailability = async (): Promise<boolean> => {
        const ok = await service.isAvailable();
        patchState(store, { available: ok });
        return ok;
      };

      const updateStoredSession = async (session: IAuthAccessToken | null): Promise<void> => {
        if (!service.isNative() || !store.enabled() || !session?.Token) return;
        try {
          await service.storeSession(session);
        } catch {
          // Best-effort refresh of the Keychain copy — a stale token self-refreshes on use.
        }
      };

      const enable = async (session: IAuthAccessToken | null): Promise<boolean> => {
        if (!service.isNative() || !session?.Token) return false;

        patchState(store, { ...setPropLoading('enable') });
        try {
          await service.verifyIdentity({
            reason: translate.instant('LOGIN.BIOMETRICS.ENABLE_REASON'),
            title: translate.instant('LOGIN.BIOMETRICS.ENABLE_TITLE'),
            subtitle: translate.instant('LOGIN.BIOMETRICS.ENABLE_SUBTITLE'),
            maxAttempts: 3,
            useFallback: true,
          });
          await service.storeSession(session);

          const person = authStore.person();
          const info: Partial<IBiometricInfo> = {
            Enabled: true,
            PersonId: session.PersonId ?? person?.PersonId ?? null,
            UserName: person ? personDisplayName(person) : null,
            UserEmail: person?.Email ?? null,
          };
          service.patchInfo(info);
          patchState(store, { enabled: true, unlocked: true, ...setPropLoaded('enable') });
          return true;
        } catch (error) {
          patchState(store, { ...setPropError('enable', error) });
          return false;
        }
      };

      const disable = async (): Promise<void> => {
        patchState(store, { unlocked: false, enabled: false });
        service.patchInfo({ Enabled: false });
        await service.deleteSession();
      };

      const promptToEnable = async (session: IAuthAccessToken | null): Promise<void> => {
        if (store.enabled() || !session?.Token) return;
        if (!(await checkAvailability())) return;
        await enable(session);
      };

      return {
        checkAvailability,
        updateStoredSession,
        enable,
        disable,
        promptToEnable,

        /**
         * After a fresh password login: drop any prior enrollment (it may belong to a
         * different account) and offer biometrics for the account that just logged in.
         */
        offerAfterLogin: async (session: IAuthAccessToken | null): Promise<void> => {
          await disable();
          await promptToEnable(session);
        },

        /** Prompts biometrics and returns the stored session on success. */
        unlock: async (): Promise<IAuthAccessToken | null> => {
          if (!service.isNative() || !store.enabled()) return null;

          patchState(store, { ...setPropLoading('unlock') });
          try {
            await service.verifyIdentity({
              reason: translate.instant('LOGIN.BIOMETRICS.UNLOCK_REASON'),
              title: translate.instant('LOGIN.BIOMETRICS.UNLOCK_TITLE'),
              maxAttempts: 3,
              useFallback: true,
            });
            const session = await service.readSession();
            patchState(store, { unlocked: true, ...setPropLoaded('unlock') });
            return session;
          } catch (error) {
            patchState(store, { ...setPropError('unlock', error) });
            return null;
          }
        },

        /** Marks the current app session as authenticated (e.g. after a password login). */
        markUnlocked: (): void => {
          patchState(store, { unlocked: true });
        },

        /** Locks the gate (e.g. on logout) WITHOUT removing the stored credential. */
        lock: (): void => {
          patchState(store, { unlocked: false, enabled: service.readInfo().Enabled });
        },

        /** Keeps the Keychain session in sync as the token refreshes (silent, no prompt). */
        watchSession: rxMethod<IAuthAccessToken | null>(
          pipe(
            tap((session: IAuthAccessToken | null) => {
              if (!service.isNative() || !store.enabled()) return;
              void updateStoredSession(session);
              if (session?.PersonId) service.patchInfo({ PersonId: session.PersonId });
            })
          )
        ),

        /** Persists the display identity so the welcome card can render while logged out. */
        watchIdentity: rxMethod<IPerson | null>(
          pipe(
            tap((person: IPerson | null) => {
              if (!service.isNative() || !person) return;
              service.patchInfo({
                UserName: personDisplayName(person),
                UserEmail: person.Email ?? null,
                PersonId: person.PersonId ?? null,
              });
            })
          )
        ),
      };
    }
  ),
  withHooks({
    onInit(store, service = inject(BiometricService), authStore = inject(AuthStore)) {
      // Stay fully dormant unless the app opted in via APP_BIOMETRIC_LOGIN, so
      // non-opted native apps never run the availability probe or persist identity.
      if (!(inject(APP_BIOMETRIC_LOGIN, { optional: true }) ?? false)) return;
      patchState(store, { enabled: service.readInfo().Enabled });
      void store.checkAvailability();
      store.watchSession(authStore.observableAuthData());
      store.watchIdentity(toObservable(authStore.person));
    },
  })
);
